import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { LANG_NAMES } from "@hudhud/core";
import { api, type QueueJob } from "../api.js";
import { Icon } from "../components/Icon.js";
import { useToast } from "../components/Toast.js";
import { usePhone } from "../hooks/useMedia.js";

/** The jobs the pause switch holds back, and the ones the queue page groups first. */
const RENDERS = new Set(["render", "renderNoMusic", "renderLang"]);
const TRANSLATIONS = new Set(["localise", "translate"]);
const KIND_LABEL: Record<string, string> = {
  render: "Render", renderNoMusic: "Render without BG audio", renderLang: "Render", localise: "Translate", translate: "Translate", pipeline: "Process the lecture", reselect: "Find clips again",
  selectMore: "Find more clips", analyse: "Analyse", captions: "Captions", metadata: "Titles and descriptions", cutout: "Cut out the speaker", cleanVoice: "Voice cleaning", ingest: "Prepare the preview",
  timelineLite: "Refresh the preview", cover: "Cover", pack: "Export pack", overlayPlan: "Plan pictures", overlayImage: "Make a picture", direct: "Direct the clip", lengthReview: "Review the length", transcribe: "Transcribe", select: "Find clips",
};
const langName = (l: unknown) => (typeof l === "string" ? (LANG_NAMES as Record<string, string>)[l] ?? l : "");
/** What a job is, in words: "Render · Arabic", "Translate · Arabic, Turkish". */
export function jobTitle(j: QueueJob): string {
  const kind = KIND_LABEL[j.type] ?? j.type;
  if (j.type === "renderLang") return `${kind} · ${langName(j.payload.lang)}`;
  if (j.type === "localise" && Array.isArray(j.payload.langs)) return `${kind} · ${(j.payload.langs as unknown[]).map(langName).join(", ")}`;
  return kind;
}
const STATUS_LABEL: Record<string, string> = { queued: "Waiting", running: "Running", done: "Done", failed: "Failed", cancelled: "Cancelled" };
const ago = (iso: string | null | undefined) => { if (!iso) return ""; const s = Math.max(0, Math.round((Date.now() - Date.parse(iso)) / 1000)); return s < 60 ? `${s}s ago` : s < 3600 ? `${Math.round(s / 60)} min ago` : `${Math.round(s / 3600)} h ago`; };

/** How many jobs the owner is waiting on right now (renders and translations, waiting or running): the badge in the navigation. */
export function useQueueCount(): number {
  const [n, setN] = useState(0);
  useEffect(() => {
    let alive = true;
    const tick = () => api.queue().then((q) => { if (alive) setN(q.jobs.filter((j) => (j.status === "queued" || j.status === "running") && (RENDERS.has(j.type) || TRANSLATIONS.has(j.type))).length); }).catch(() => undefined);
    tick();
    const t = setInterval(tick, 5000);
    return () => { alive = false; clearInterval(t); };
  }, []);
  return n;
}

const Row: React.FC<{ j: QueueJob; onChange: () => void }> = ({ j, onChange }) => {
  const toast = useToast();
  const phone = usePhone();
  const live = j.status === "queued" || j.status === "running";
  const act = async (what: "cancel" | "next" | "retry") => {
    try { if (what === "cancel") await api.cancelJob(j.id); else if (what === "next") await api.runNext(j.id); else await api.retry(j.id); onChange(); }
    catch (e) { toast((e as Error).message); }
  };
  const tone = j.status === "running" ? "gold" : j.status === "failed" ? "warn" : j.status === "done" ? "green" : "";
  const btn: React.CSSProperties = phone ? { minHeight: 44, minWidth: 44 } : {};
  return (
    <div className="qrow" data-job={j.id} data-status={j.status}>
      <div className="col" style={{ gap: 2, flex: 1, minWidth: 0 }}>
        <div className="row" style={{ gap: 8, alignItems: "center", flexWrap: "wrap" }}>
          <b style={{ fontSize: 13 }}>{jobTitle(j)}</b>
          <span className={`chip ${tone}`} style={{ fontSize: 10.5 }}>{STATUS_LABEL[j.status] ?? j.status}{j.status === "running" && j.progress > 0 ? ` · ${Math.round(j.progress * 100)}%` : ""}</span>
          {j.attempts > 1 && live ? <span className="hint">attempt {j.attempts}</span> : null}
        </div>
        <span className="hint" style={{ overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {j.clipId ? <Link to={`/clips/${j.videoId}/${j.clipId}`} style={{ color: "var(--text-2)", fontWeight: 600 }}>{j.clipTitle || j.clipId}</Link> : null}{j.clipId && j.lectureTitle ? " · " : ""}{j.lectureTitle ?? (j.clipId ? "" : j.videoId)}
          {!live ? ` · ${ago(j.finishedAt ?? j.createdAt)}` : j.status === "running" ? ` · started ${ago(j.startedAt)}` : ` · queued ${ago(j.createdAt)}`}
        </span>
        {j.status === "running" ? <div className="qbar"><div style={{ width: `${Math.round(j.progress * 100)}%` }} /></div> : null}
        {j.error && j.status !== "running" ? <span className="hint" style={{ color: j.status === "failed" ? "var(--warn)" : "var(--muted)" }}>{j.error.split("\n")[0].slice(0, 140)}</span> : null}
      </div>
      {live || j.status === "failed" || j.status === "cancelled" ? <div className="row qacts" style={{ gap: 6, flexShrink: 0 }}>
        {j.status === "queued" ? <button className="btn sm" style={btn} onClick={() => act("next")} title="Move to the front">Run next</button> : null}
        {live ? <button className="btn sm quiet" style={btn} onClick={() => act("cancel")}><Icon name="trash" size={12} />Cancel</button> : null}
        {j.status === "failed" || j.status === "cancelled" ? <button className="btn sm" style={btn} onClick={() => act("retry")}><Icon name="refresh" size={12} />Retry</button> : null}
      </div> : null}
    </div>
  );
};

/** The global queue: renders and translations first, then everything else the studio is doing, then what finished lately. Pause holds the heavy work; running jobs can be stopped. */
export const Queue: React.FC = () => {
  const toast = useToast();
  const phone = usePhone();
  const [paused, setPaused] = useState(false);
  const [jobs, setJobs] = useState<QueueJob[]>([]);
  const [loaded, setLoaded] = useState(false);
  const load = () => api.queue().then((q) => { setPaused(q.paused); setJobs(q.jobs); setLoaded(true); }).catch((e) => toast(e.message));
  useEffect(() => { load(); const t = setInterval(load, 2500); return () => clearInterval(t); }, []);
  const live = jobs.filter((j) => j.status === "queued" || j.status === "running");
  const renders = live.filter((j) => RENDERS.has(j.type));
  const translations = live.filter((j) => TRANSLATIONS.has(j.type));
  const other = live.filter((j) => !RENDERS.has(j.type) && !TRANSLATIONS.has(j.type) && j.type !== "timelineLite" && (j.priority ?? 0) >= 0);
  const recent = jobs.filter((j) => j.status !== "queued" && j.status !== "running");
  const waiting = renders.length + translations.length;
  const togglePause = async () => { try { const r = paused ? await api.resumeQueue() : await api.pauseQueue(); setPaused(r.paused); toast(r.paused ? "Paused: renders and translations wait" : "Resumed"); load(); } catch (e) { toast((e as Error).message); } };
  const cancelWaiting = async () => { try { const r = await api.cancelQueued(); toast(r.cancelled ? `${r.cancelled} waiting job${r.cancelled === 1 ? "" : "s"} cancelled` : "Nothing was waiting"); load(); } catch (e) { toast((e as Error).message); } };
  const section = (title: string, list: QueueJob[], empty: string) => (
    <section className="qsection" aria-label={title}>
      <h3>{title}<span className="hint" style={{ fontWeight: 600 }}>{list.length ? ` · ${list.length}` : ""}</span></h3>
      {list.length ? list.map((j) => <Row key={j.id} j={j} onChange={load} />) : <span className="hint">{empty}</span>}
    </section>
  );
  const big: React.CSSProperties = phone ? { minHeight: 44 } : {};
  return (
    <main className="page queue" style={{ maxWidth: 860 }}>
      <div className="row" style={{ gap: 10, alignItems: "center", flexWrap: "wrap", marginBottom: 12 }}>
        <h1 style={{ margin: 0, fontSize: 22 }}>Queue</h1>
        <span className="hint">{paused ? "Paused: renders and translations wait; previews and quick work carry on." : waiting ? `${waiting} render${waiting === 1 ? "" : "s"} or translation${waiting === 1 ? "" : "s"} in the queue.` : "Nothing heavy is queued."}</span>
        <span className="spacer" />
        <button className={`btn sm ${paused ? "gold" : ""}`} style={big} onClick={togglePause} aria-pressed={paused}><Icon name={paused ? "play" : "pause"} size={13} />{paused ? "Resume" : "Pause"}</button>
        <button className="btn sm quiet" style={big} onClick={cancelWaiting} disabled={!live.some((j) => j.status === "queued")}>Cancel all waiting</button>
      </div>
      {!loaded ? <span className="hint">Loading…</span> : null}
      {section("Rendering", renders, "No render waiting or running.")}
      {section("Translating", translations, "No translation waiting or running.")}
      {section("Preparing", other, "Nothing else is running.")}
      {section("Recent", recent.slice(0, 20), "Nothing finished lately.")}
    </main>
  );
};
